import { useState, useEffect } from "react";
import { getApiUrl } from "../utils/api";
const API = getApiUrl();
import { useParams, useSearchParams } from "react-router-dom";
import { Heart, Gift, CreditCard, ArrowLeft, QrCode, FileText, ShoppingCart, Trash2, Copy, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";


export default function CheckoutPage() {
  const { siteId } = useParams();
  const [searchParams] = useSearchParams();
  
  const [site, setSite] = useState(null);
  const [gift, setGift] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showPixManual, setShowPixManual] = useState(false);
  const [pixCopied, setPixCopied] = useState(false);
  const [formData, setFormData] = useState({
    amount: "",
    payer_name: "",
    payer_email: "",
    message: "",
    payment_method: "card",
    installments: "1"
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch site info including PIX config
        const siteRes = await fetch(`${API}/sites/${siteId}/gifts?active_only=true`);
        let siteData = { site_id: siteId };
        
        // Try to get full site info by slug
        try {
          // Find slug from URL or use a workaround
          const urlPath = window.location.pathname;
          const slugMatch = urlPath.match(/checkout\/([^\/]+)/);
          if (slugMatch) {
            const fullSiteRes = await fetch(`${API}/sites/slug/${slugMatch[1]}`);
            if (fullSiteRes.ok) {
              siteData = await fullSiteRes.json();
            }
          }
        } catch {}

        // Alternatively, try to get site by ID from gifts endpoint response
        if (siteRes.ok) {
          const giftsData = await siteRes.json();
          
          // Check for multiple gifts in URL (cart)
          const giftParams = searchParams.getAll('gift');
          const qtyParams = searchParams.getAll('qty');
          
          if (giftParams.length > 0) {
            const items = [];
            let totalAmount = 0;
            
            giftParams.forEach((giftId, index) => {
              const foundGift = giftsData.find(g => g.gift_id === giftId);
              if (foundGift) {
                const qty = parseInt(qtyParams[index]) || 1;
                items.push({ ...foundGift, quantity: qty });
                totalAmount += foundGift.price * qty;
              }
            });
            
            if (items.length > 0) {
              setCartItems(items);
              setFormData(prev => ({ ...prev, amount: totalAmount.toString() }));
              
              if (items.length === 1) {
                setGift(items[0]);
              }
            }
          }
        }
        
        // Try to get site info from a dedicated endpoint
        try {
          const siteInfoRes = await fetch(`${API}/sites/${siteId}/info`);
          if (siteInfoRes.ok) {
            siteData = await siteInfoRes.json();
          }
        } catch {}
        
        setSite(siteData);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [siteId, searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const copyPixKey = async () => {
    if (site?.pix_config?.key_value) {
      await navigator.clipboard.writeText(site.pix_config.key_value);
      setPixCopied(true);
      toast.success("Chave PIX copiada!");
      setTimeout(() => setPixCopied(false), 3000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.amount || parseFloat(formData.amount) < 1) {
      toast.error("Valor mínimo é R$ 1,00");
      return;
    }

    // Check if PIX manual is selected and enabled
    if (formData.payment_method === "pix" && site?.pix_config?.enabled) {
      setShowPixManual(true);
      return;
    }

    setProcessing(true);

    try {
      // Prepare gift_ids for cart items
      const giftIds = cartItems.length > 0 
        ? cartItems.map(item => ({ gift_id: item.gift_id, quantity: item.quantity }))
        : gift ? [{ gift_id: gift.gift_id, quantity: 1 }] : [];

      const res = await fetch(`${API}/sites/${siteId}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gift_id: gift?.gift_id || null,
          gift_items: giftIds,
          amount: parseFloat(formData.amount),
          payer_name: formData.payer_name,
          payer_email: formData.payer_email,
          message: formData.message,
          origin_url: window.location.origin,
          payment_method: formData.payment_method,
          installments: parseInt(formData.installments)
        })
      });

      if (res.ok) {
        const data = await res.json();
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Erro ao processar pagamento');
      }
    } catch (error) {
      toast.error(error.message);
      setProcessing(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const calculateFee = () => {
    const amount = parseFloat(formData.amount) || 0;
    return amount * 0.03;
  };

  const calculateTotal = () => {
    return parseFloat(formData.amount) || 0;
  };

  const calculateInstallmentValue = () => {
    const total = calculateTotal();
    const installments = parseInt(formData.installments) || 1;
    return total / installments;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="w-16 h-16 border-4 border-[#F3C9CF] border-t-[#B3121B] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] relative">
      <div className="absolute inset-0 noise-overlay"></div>
      
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-[#F3C9CF]/30 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => window.history.back()}
            className="text-[#6E0F1A]"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Voltar
          </Button>
          <div className="flex items-center gap-2">
            <Heart className="w-6 h-6 text-[#B3121B] fill-[#B3121B]" />
            <span className="font-cursive text-xl text-[#6E0F1A]">Fiesta Lab</span>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12 relative z-10">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#F3C9CF] rounded-full flex items-center justify-center mx-auto mb-4">
            {cartItems.length > 1 ? (
              <ShoppingCart className="w-8 h-8 text-[#B3121B]" />
            ) : (
              <Gift className="w-8 h-8 text-[#B3121B]" />
            )}
          </div>
          <h1 className="font-serif text-3xl text-[#6E0F1A] mb-2">
            {cartItems.length > 1 
              ? `Carrinho (${cartItems.length} itens)` 
              : gift 
                ? `Presentear: ${gift.name}` 
                : 'Fazer uma Contribuição'}
          </h1>
          <p className="text-[#6E0F1A]/70">
            Sua contribuição será recebida diretamente pelos noivos
          </p>
        </div>

        <Card className="card-dashboard">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Cart items - multiple gifts */}
              {cartItems.length > 1 && (
                <div className="space-y-3 mb-6">
                  <Label className="text-[#6E0F1A] font-semibold">Presentes selecionados:</Label>
                  {cartItems.map((item) => {
                    const imageUrl = item.image_url ? 
                      (item.image_url.startsWith('http') ? item.image_url : `${process.env.REACT_APP_BACKEND_URL}${item.image_url}`) 
                      : null;
                    
                    return (
                      <div key={item.gift_id} className="flex items-center gap-4 p-3 bg-[#FAFAFA] rounded-lg">
                        {imageUrl && (
                          <img
                            src={imageUrl}
                            alt={item.name}
                            className="w-12 h-12 object-cover rounded-lg flex-shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-[#6E0F1A] truncate">{item.name}</h4>
                          <p className="text-sm text-[#6E0F1A]/60">
                            {item.quantity > 1 && `${item.quantity}x `}
                            {formatCurrency(item.price * item.quantity)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  <div className="flex justify-between items-center pt-3 border-t border-[#F3C9CF]/30">
                    <span className="font-medium text-[#6E0F1A]">Total:</span>
                    <span className="font-semibold text-lg text-[#6E0F1A]">
                      {formatCurrency(cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0))}
                    </span>
                  </div>
                </div>
              )}
              
              {/* Single gift info */}
              {gift && cartItems.length <= 1 && (
                <div className="flex items-center gap-4 p-4 bg-[#FAFAFA] rounded-lg mb-6">
                  {gift.image_url && (
                    <img
                      src={gift.image_url.startsWith('http') ? gift.image_url : `${process.env.REACT_APP_BACKEND_URL}${gift.image_url}`}
                      alt={gift.name}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                  )}
                  <div>
                    <h3 className="font-semibold text-[#6E0F1A]">{gift.name}</h3>
                    <p className="text-[#567D50] font-medium">{formatCurrency(gift.price)}</p>
                    {gift.allow_partial && (
                      <p className="text-xs text-[#6E0F1A]/50">Contribuição parcial permitida</p>
                    )}
                  </div>
                </div>
              )}

              {/* Amount */}
              <div>
                <Label htmlFor="amount" className="text-[#6E0F1A]">
                  {gift ? 'Valor da Contribuição *' : 'Quanto deseja contribuir? *'}
                </Label>
                <div className="relative mt-2">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6E0F1A]/60">R$</span>
                  <Input
                    id="amount"
                    name="amount"
                    type="number"
                    step="0.01"
                    min="1"
                    value={formData.amount}
                    onChange={handleChange}
                    required
                    className="input-wedding pl-12 text-lg"
                    placeholder="0,00"
                    data-testid="checkout-amount-input"
                  />
                </div>
              </div>

              {/* Payment Method */}
              <div>
                <Label className="text-[#6E0F1A] mb-3 block">Forma de Pagamento</Label>
                <RadioGroup
                  value={formData.payment_method}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, payment_method: value, installments: "1" }))}
                  className="grid grid-cols-3 gap-4"
                >
                  <div>
                    <RadioGroupItem value="card" id="card" className="peer sr-only" />
                    <Label
                      htmlFor="card"
                      className="flex flex-col items-center justify-center rounded-xl border-2 border-[#F3C9CF] bg-white p-4 cursor-pointer hover:border-[#B3121B] peer-data-[state=checked]:border-[#B3121B] peer-data-[state=checked]:bg-[#B3121B]/5 transition-all"
                    >
                      <CreditCard className="w-8 h-8 text-[#B3121B] mb-2" />
                      <span className="font-medium text-[#6E0F1A]">Cartão</span>
                      <span className="text-xs text-[#6E0F1A]/60">Até 4x sem juros</span>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem value="pix" id="pix" className="peer sr-only" />
                    <Label
                      htmlFor="pix"
                      className="flex flex-col items-center justify-center rounded-xl border-2 border-[#F3C9CF] bg-white p-4 cursor-pointer hover:border-[#B3121B] peer-data-[state=checked]:border-[#B3121B] peer-data-[state=checked]:bg-[#B3121B]/5 transition-all"
                    >
                      <QrCode className="w-8 h-8 text-[#567D50] mb-2" />
                      <span className="font-medium text-[#6E0F1A]">PIX</span>
                      <span className="text-xs text-[#6E0F1A]/60">Instantâneo</span>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem value="boleto" id="boleto" className="peer sr-only" />
                    <Label
                      htmlFor="boleto"
                      className="flex flex-col items-center justify-center rounded-xl border-2 border-[#F3C9CF] bg-white p-4 cursor-pointer hover:border-[#B3121B] peer-data-[state=checked]:border-[#B3121B] peer-data-[state=checked]:bg-[#B3121B]/5 transition-all"
                    >
                      <FileText className="w-8 h-8 text-[#6E0F1A] mb-2" />
                      <span className="font-medium text-[#6E0F1A]">Boleto</span>
                      <span className="text-xs text-[#6E0F1A]/60">Até 3 dias úteis</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Installments - Only for card */}
              {formData.payment_method === "card" && parseFloat(formData.amount) >= 20 && (
                <div>
                  <Label htmlFor="installments" className="text-[#6E0F1A]">Parcelamento</Label>
                  <Select
                    value={formData.installments}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, installments: value }))}
                  >
                    <SelectTrigger className="input-wedding mt-2">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1x de {formatCurrency(calculateTotal())} (à vista)</SelectItem>
                      <SelectItem value="2">2x de {formatCurrency(calculateTotal() / 2)} sem juros</SelectItem>
                      <SelectItem value="3">3x de {formatCurrency(calculateTotal() / 3)} sem juros</SelectItem>
                      <SelectItem value="4">4x de {formatCurrency(calculateTotal() / 4)} sem juros</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Payer info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="payer_name" className="text-[#6E0F1A]">Seu Nome</Label>
                  <Input
                    id="payer_name"
                    name="payer_name"
                    value={formData.payer_name}
                    onChange={handleChange}
                    className="input-wedding mt-2"
                    placeholder="Como deseja ser identificado"
                    data-testid="checkout-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="payer_email" className="text-[#6E0F1A]">Seu E-mail</Label>
                  <Input
                    id="payer_email"
                    name="payer_email"
                    type="email"
                    value={formData.payer_email}
                    onChange={handleChange}
                    className="input-wedding mt-2"
                    placeholder="Para enviarmos o comprovante"
                    data-testid="checkout-email-input"
                  />
                </div>
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="message" className="text-[#6E0F1A]">Mensagem para os Noivos</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="input-wedding mt-2"
                  placeholder="Deixe uma mensagem especial..."
                  data-testid="checkout-message-input"
                />
              </div>

              {/* Summary */}
              <div className="border-t border-[#F3C9CF]/30 pt-6 space-y-3">
                <div className="flex justify-between text-[#6E0F1A]/70">
                  <span>Valor do Presente</span>
                  <span>{formatCurrency(parseFloat(formData.amount) || 0)}</span>
                </div>
                <div className="flex justify-between text-[#6E0F1A]/50 text-sm">
                  <span>Taxa da Plataforma (3%)</span>
                  <span>{formatCurrency(calculateFee())}</span>
                </div>
                {formData.payment_method === "card" && parseInt(formData.installments) > 1 && (
                  <div className="flex justify-between text-[#6E0F1A]/70">
                    <span>Parcelamento</span>
                    <span>{formData.installments}x de {formatCurrency(calculateInstallmentValue())}</span>
                  </div>
                )}
                <div className="flex justify-between text-[#6E0F1A] font-semibold text-lg pt-3 border-t border-[#F3C9CF]/30">
                  <span>Valor a Pagar</span>
                  <span>{formatCurrency(calculateTotal())}</span>
                </div>
                <p className="text-xs text-[#6E0F1A]/50 text-center">
                  Os noivos receberão {formatCurrency(calculateTotal() - calculateFee())}
                </p>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={processing || !formData.amount}
                className="w-full bg-[#B3121B] hover:bg-[#9A0F17] text-white rounded-full py-6 text-lg"
                data-testid="checkout-submit-btn"
              >
                {formData.payment_method === "pix" ? (
                  <>
                    <QrCode className="w-5 h-5 mr-2" />
                    {processing ? 'Processando...' : 'Pagar com PIX'}
                  </>
                ) : formData.payment_method === "boleto" ? (
                  <>
                    <FileText className="w-5 h-5 mr-2" />
                    {processing ? 'Processando...' : 'Gerar Boleto'}
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    {processing ? 'Processando...' : 'Pagar com Cartão'}
                  </>
                )}
              </Button>

              <p className="text-xs text-[#6E0F1A]/50 text-center">
                {formData.payment_method === "pix" && site?.pix_config?.enabled 
                  ? "Você será direcionado para os dados do PIX"
                  : "Pagamento processado de forma segura pelo Stripe"
                }
              </p>
            </form>
          </CardContent>
        </Card>
      </main>

      {/* PIX Manual Modal */}
      {showPixManual && site?.pix_config && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl animate-fade-in">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#F3C9CF] rounded-full flex items-center justify-center mx-auto mb-4">
                <QrCode className="w-8 h-8 text-[#B3121B]" />
              </div>
              <h2 className="font-serif text-2xl text-[#6E0F1A] mb-2">Pagar com PIX</h2>
              <p className="text-[#6E0F1A]/60">
                Valor: <span className="font-bold text-[#B3121B]">{formatCurrency(formData.amount)}</span>
              </p>
            </div>

            {/* QR Code */}
            {site.pix_config.qr_code_image && (
              <div className="flex justify-center mb-6">
                <img
                  src={site.pix_config.qr_code_image.startsWith('http') 
                    ? site.pix_config.qr_code_image 
                    : `${process.env.REACT_APP_BACKEND_URL}${site.pix_config.qr_code_image}`
                  }
                  alt="QR Code PIX"
                  className="w-48 h-48 object-contain border-2 border-[#F3C9CF] rounded-xl bg-white p-2"
                />
              </div>
            )}

            {/* PIX Key */}
            <div className="bg-[#FFFAF5] rounded-xl p-4 mb-4">
              <p className="text-sm text-[#6E0F1A]/60 mb-1">
                {site.pix_config.holder_name && (
                  <span className="font-medium text-[#6E0F1A]">{site.pix_config.holder_name}</span>
                )}
              </p>
              <p className="text-xs text-[#6E0F1A]/50 mb-2">
                Chave PIX ({
                  site.pix_config.key_type === "cpf" ? "CPF" :
                  site.pix_config.key_type === "cnpj" ? "CNPJ" :
                  site.pix_config.key_type === "email" ? "E-mail" :
                  site.pix_config.key_type === "phone" ? "Telefone" : "Chave Aleatória"
                })
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-white px-4 py-3 rounded-lg text-[#6E0F1A] font-mono text-sm border border-[#F3C9CF] break-all">
                  {site.pix_config.key_value}
                </code>
                <Button
                  type="button"
                  onClick={copyPixKey}
                  className={`${pixCopied ? 'bg-green-500' : 'bg-[#B3121B]'} hover:bg-[#9A0F17] text-white px-4`}
                >
                  {pixCopied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </Button>
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>Como pagar:</strong>
              </p>
              <ol className="text-sm text-blue-700 mt-2 space-y-1 list-decimal list-inside">
                <li>Abra o app do seu banco</li>
                <li>Escaneie o QR Code ou copie a chave PIX</li>
                <li>Confirme o valor de {formatCurrency(formData.amount)}</li>
                <li>Finalize o pagamento</li>
              </ol>
            </div>

            {/* Message for couple */}
            {formData.message && (
              <div className="bg-[#F3C9CF]/30 rounded-lg p-4 mb-6">
                <p className="text-sm text-[#6E0F1A]/60 mb-1">Sua mensagem:</p>
                <p className="text-[#6E0F1A] italic">"{formData.message}"</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowPixManual(false)}
                className="flex-1 border-[#B3121B] text-[#B3121B] hover:bg-[#B3121B] hover:text-white"
              >
                Voltar
              </Button>
              <Button
                onClick={() => {
                  toast.success("Obrigado! Aguardamos a confirmação do seu PIX.");
                  setTimeout(() => window.history.back(), 2000);
                }}
                className="flex-1 bg-[#B3121B] hover:bg-[#9A0F17] text-white"
              >
                Já fiz o PIX
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
